import React, { useState } from 'react';
import { clamp } from 'lodash';

import { Button } from 'shadcn/components/ui/Button';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Slider } from 'shadcn/components/ui/Slider';

/**
 * TransferInventoryControl - Component for transferring inventory between channels
 *
 * @param {Object} props
 * @param {string} props.primaryLabel - Label for the primary channel
 * @param {string} props.secondaryLabel - Label for the secondary channel
 * @param {number} props.initialPrimary - Initial primary (left side of slider)remaining inventory
 * @param {number} props.initialSecondary - Initial secondary (right side of slider) remaining inventory
 * @param {Function} [props.onSaveTransfer] - Callback when Save Transfer is clicked
 */
export function TransferInventoryControl({
    initialPrimary = 0,
    initialSecondary = 0,
    onSaveTransfer,
    primaryLabel,
    secondaryLabel,
}) {
    // Calculate the total inventory (sum of both, preserving sign)
    const totalInventory = initialPrimary + initialSecondary;

    // Slider bounds ensure:
    // - primary max value is the greater of what it already has (initialPrimary) or all available inventory combined (totalInventory)
    // - primary min can go as low as the smaller of: zero, or what it already has (initialPrimary - if negative)
    const sliderMax = Math.max(initialPrimary, totalInventory);
    const sliderMin = Math.min(initialPrimary, 0);

    const isTransferAllowed = !(initialPrimary < 0 && initialSecondary < 0);

    const [primaryAmount, setPrimaryAmount] = useState(initialPrimary);
    const [primaryInput, setPrimaryInput] = useState(String(initialPrimary));
    const [secondaryInput, setSecondaryInput] = useState(String(initialSecondary));

    // Calculate changes from initial values
    const primaryChange = primaryAmount - initialPrimary;
    const secondaryChange = totalInventory - primaryAmount - initialSecondary;

    // Format change display
    const formatChange = (change) => {
        if (change === 0) return null;
        const sign = change > 0 ? '+' : '';
        return `${sign}${change.toLocaleString()}`;
    };

    // Apply the primary amount to the state and input fields
    const applyPrimaryAmount = (value) => {
        setPrimaryAmount(value);
        setPrimaryInput(String(value));
        setSecondaryInput(String(totalInventory - value));
    };

    const parseInputOrFallback = (input, fallback) => {
        if (input.trim() === '') {
            return fallback;
        }

        const parsed = Number(input);

        if (Number.isNaN(parsed)) {
            return fallback;
        }
        return parsed;
    };

    const commitPrimaryInput = () => {
        const parsed = parseInputOrFallback(primaryInput, primaryAmount);
        const clamped = clamp(parsed, sliderMin, sliderMax);

        applyPrimaryAmount(clamped);
    };

    const commitSecondaryInput = () => {
        const parsed = parseInputOrFallback(secondaryInput, totalInventory - primaryAmount);
        const newPrimary = totalInventory - parsed;
        const clamped = clamp(newPrimary, sliderMin, sliderMax);

        applyPrimaryAmount(clamped);
    };

    const handleSaveTransfer = () => {
        if (typeof onSaveTransfer !== 'function') {
            return;
        }

        commitPrimaryInput(); // ensure latest value

        if (!isTransferAllowed || primaryChange === 0) {
            return;
        }

        onSaveTransfer({
            amount: Math.abs(primaryChange),
            fromChannel: primaryChange < 0 ? primaryLabel : secondaryLabel,
            toChannel: primaryChange > 0 ? primaryLabel : secondaryLabel,
        });
    };

    const handlePrimaryInputChange = (e) => {
        setPrimaryInput(e.target.value);
    };

    const handleSecondaryInputChange = (e) => {
        setSecondaryInput(e.target.value);
    };

    const handleSliderChange = (value) => {
        applyPrimaryAmount(value);
    };

    const handleKeyDown = (e, inputType) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (inputType === 'primary') {
                commitPrimaryInput();
            } else if (inputType === 'secondary') {
                commitSecondaryInput();
            }
            e.currentTarget.blur();
        } else if (e.key === 'Escape') {
            applyPrimaryAmount(primaryAmount);
            e.currentTarget.blur();
        }
    };

    const handleResetTransfer = () => {
        applyPrimaryAmount(initialPrimary);
    };

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
            {/* Header: Title on left, Available to Transfer on right */}
            <div className="flex justify-between items-start mb-4">
                <div className="text-left">
                    <h2 className="text-md font-bold text-gray-900 mb-1">Transfer Inventory Between Channels</h2>
                    <p className="text-sm text-gray-600">Reallocate units from one channel to another</p>
                </div>
                <div className="text-right">
                    <div className="text-md text-gray-600">
                        Available to Transfer:{' '}
                        <span className="font-semibold text-gray-900">{totalInventory.toLocaleString()} units</span>
                    </div>
                </div>
            </div>

            {!isTransferAllowed ? (
                <div className="text-center py-8">
                    <p className="text-base text-gray-600">
                        {primaryLabel} and {secondaryLabel} are both empty. There are no units available to transfer.
                    </p>
                </div>
            ) : (
                <>
                    {/* Transfer Split Label */}
                    <div className="mb-2 text-left">
                        <Label htmlFor="transfer-slider" className="text-md font-bold text-gray-900">
                            Transfer Split
                        </Label>
                    </div>

                    {/* Slider */}
                    <div className="mb-2">
                        <Slider
                            id="transfer-slider"
                            value={[primaryAmount]}
                            onValueChange={(value) => handleSliderChange(value[0])}
                            max={sliderMax}
                            min={sliderMin}
                            step={1}
                            className="w-full"
                        />
                    </div>

                    {/* Slider Labels */}
                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                        <span>0% {primaryLabel}</span>
                        <span>100% {primaryLabel}</span>
                    </div>

                    {/* Change Summary */}
                    {(primaryChange !== 0 || secondaryChange !== 0) && (
                        <div className="text-center text-base font-semibold mb-4">
                            <span className={primaryChange > 0 ? 'text-green-600' : 'text-red-600'}>
                                {primaryLabel}: {formatChange(primaryChange)}
                            </span>
                            <span className="text-gray-600 mx-2">|</span>
                            <span className={secondaryChange > 0 ? 'text-green-600' : 'text-red-600'}>
                                {secondaryLabel}: {formatChange(secondaryChange)}
                            </span>
                        </div>
                    )}

                    {/* Transfer Amounts */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                        <div>
                            <Label
                                htmlFor="primary-input"
                                className="block text-base font-bold text-gray-900 mb-2 text-left"
                            >
                                {primaryLabel}
                            </Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    id="primary-input"
                                    type="number"
                                    value={primaryInput}
                                    className="w-full px-4 py-4 h-14 bg-gray-50 text-gray-900 text-lg tabular-nums"
                                    onChange={handlePrimaryInputChange}
                                    onBlur={commitPrimaryInput}
                                    onKeyDown={(e) => handleKeyDown(e, 'primary')}
                                />
                                {formatChange(primaryChange) && (
                                    <span
                                        className={`text-base font-semibold whitespace-nowrap ${primaryChange > 0 ? 'text-green-600' : 'text-red-600'}`}
                                    >
                                        {formatChange(primaryChange)}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div>
                            <Label
                                htmlFor="secondary-input"
                                className="block text-base font-bold text-gray-900 mb-2 text-left"
                            >
                                {secondaryLabel}
                            </Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    id="secondary-input"
                                    type="number"
                                    value={secondaryInput}
                                    className="w-full px-4 py-4 h-14 bg-gray-50 text-gray-900 text-lg tabular-nums"
                                    onChange={handleSecondaryInputChange}
                                    onBlur={commitSecondaryInput}
                                    onKeyDown={(e) => handleKeyDown(e, 'secondary')}
                                />
                                {formatChange(secondaryChange) && (
                                    <span
                                        className={`text-base font-semibold whitespace-nowrap ${secondaryChange > 0 ? 'text-green-600' : 'text-red-600'}`}
                                    >
                                        {formatChange(secondaryChange)}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                        <Button
                            onClick={handleSaveTransfer}
                            className="flex-1 bg-[#004693] hover:bg-[#003570] text-white py-6 text-base"
                            disabled={primaryChange === 0}
                        >
                            Save Transfer
                        </Button>
                        <Button onClick={handleResetTransfer} variant="outline" className="py-6 px-8 text-base">
                            Reset
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
}
